import React, { useEffect, useState } from "react";
import { APP_THEME } from "../theme/theme";
import Header from "../components/header";
import { apiCall } from "../utils/api";
import toast, { Toaster } from 'react-hot-toast';

function ProfileEdit() {
    const [fetching, setFetching] = useState(true);
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        mobile: "",
    });

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        try {
            const result = await apiCall(
                "profile-update",
                {
                    name: formData.name,
                    email: formData.email,
                },
                true
            );

            if (result.status) {
                toast.success("Profile updated successfully!");
            } else {
                toast.error(result.message || "An error occurred. Please try again.");
            }
        } catch (error) {
            toast.error("Network error. Please try again.");
        }
    };

    useEffect(() => {
        const fetchProfile = async () => {
            try {
                const result = await apiCall(
                    "profile-fetch",
                    {},
                    true
                );

                if (result.status && result.profile) {
                    setFormData({
                        name: result.profile.name || "",
                        email: result.profile.email || "",
                        mobile: result.profile.mobile || "",
                    });
                } else {
                    toast.error(result.message || "An error occurred. Please try again.");
                }
            } catch (error) {
                toast.error("Network error. Please try again.");
            } finally {
                setFetching(false);
            }
        }
        fetchProfile();
    }, [])

    // Loading state
    if (fetching) {
        return (
            <>
                <Header title={"Profile Edit"} showBack={true} />
                <div className="text-center py-12">
                    <div className="w-10 h-10 rounded-full animate-spin mx-auto mb-4" style={{ border: `3px solid ${APP_THEME.primaryColor}33`, borderTopColor: APP_THEME.primaryColor }} />
                    <div className="text-sm font-medium" style={{ color: APP_THEME.textSecondary, fontFamily: APP_THEME.fontFamily }}>
                        Loading profile...
                    </div>
                </div>
            </>
        );
    }

    return (
        <>
            <Header title={"Profile Edit"} showBack={true} />
            <div className="p-4">
                {/* Profile Image Section */}
                <div className="flex flex-col items-center mb-8">
                    <div className="relative w-[84px] h-[84px]">
                        <div
                            className="w-[84px] h-[84px] rounded-full flex items-center justify-center text-4xl font-bold"
                            style={{
                                background: APP_THEME.primaryColor,
                                color: APP_THEME.textOnPrimary,
                                fontFamily: APP_THEME.fontFamily,
                            }}
                        >
                            {formData.name ? formData.name.charAt(0).toUpperCase() : "U"}
                        </div>
                        <button
                            className="absolute bottom-0 right-0 w-8 h-8 rounded-full flex items-center justify-center cursor-pointer"
                            style={{
                                background: APP_THEME.bgColor,
                                border: `1.5px solid ${APP_THEME.borderColor}`,
                                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                            }}
                        >
                            <svg
                                width="16"
                                height="16"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke={APP_THEME.primaryColor}
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            >
                                <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
                                <circle cx="12" cy="13" r="4" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Form Section */}
                <div
                    className="rounded-2xl p-6 pb-4 mb-6"
                    style={{
                        background: APP_THEME.surfaceColor,
                        border: `1px solid ${APP_THEME.borderColor}`,
                        boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
                    }}
                >
                    <form onSubmit={handleSubmit}>
                        {/* Full Name */}
                        <div className="mb-4">
                            <label
                                className="block text-[13px] font-semibold mb-1.5"
                                style={{
                                    color: APP_THEME.textPrimary,
                                    fontFamily: APP_THEME.fontFamily,
                                }}
                            >
                                Full Name
                            </label>
                            <input
                                type="text"
                                className="w-full px-3.5 py-3 text-sm outline-none transition-colors duration-200 rounded-xl"
                                style={{
                                    border: `1px solid ${APP_THEME.borderColor}`,
                                    borderRadius: APP_THEME.borderRadiusInput,
                                    fontFamily: APP_THEME.fontFamily,
                                    background: APP_THEME.surfaceColor,
                                    color: APP_THEME.textPrimary,
                                }}
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                onFocus={(e) => (e.target.style.borderColor = APP_THEME.primaryColor)}
                                onBlur={(e) => (e.target.style.borderColor = APP_THEME.borderColor)}
                                required
                            />
                        </div>

                        {/* Email Address */}
                        <div className="mb-4">
                            <label
                                className="block text-[13px] font-semibold mb-1.5"
                                style={{
                                    color: APP_THEME.textPrimary,
                                    fontFamily: APP_THEME.fontFamily,
                                }}
                            >
                                Email Address
                            </label>
                            <input
                                type="email"
                                className="w-full px-3.5 py-3 text-sm outline-none transition-colors duration-200 rounded-xl"
                                style={{
                                    border: `1px solid ${APP_THEME.borderColor}`,
                                    borderRadius: APP_THEME.borderRadiusInput,
                                    fontFamily: APP_THEME.fontFamily,
                                    background: APP_THEME.surfaceColor,
                                    color: APP_THEME.textPrimary,
                                }}
                                value={formData.email}
                                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                onFocus={(e) => (e.target.style.borderColor = APP_THEME.primaryColor)}
                                onBlur={(e) => (e.target.style.borderColor = APP_THEME.borderColor)}
                                required
                            />
                        </div>

                        {/* Mobile Number (Read Only - Not Editable) */}
                        <div className="mb-6">
                            <label
                                className="block text-[13px] font-semibold mb-1.5"
                                style={{
                                    color: APP_THEME.textPrimary,
                                    fontFamily: APP_THEME.fontFamily,
                                }}
                            >
                                Mobile Number
                            </label>
                            <input
                                type="tel"
                                className="w-full px-3.5 py-3 text-sm rounded-xl cursor-not-allowed"
                                style={{
                                    border: `1px solid ${APP_THEME.borderColor}`,
                                    borderRadius: APP_THEME.borderRadiusInput,
                                    fontFamily: APP_THEME.fontFamily,
                                    background: APP_THEME.borderColor,
                                    color: APP_THEME.textSecondary,
                                }}
                                value={formData.mobile}
                                readOnly
                                disabled
                            />
                        </div>

                        {/* Update Profile Button */}
                        <button
                            type="submit"
                            className="w-full h-12 border-none rounded-lg text-[15px] font-bold cursor-pointer mt-2 transition-all duration-200 active:scale-98"
                            style={{
                                background: APP_THEME.primaryColor,
                                color: APP_THEME.textOnPrimary,
                                borderRadius: APP_THEME.borderRadiusBtn,
                                fontFamily: APP_THEME.fontFamily,
                                boxShadow: `0 4px 12px ${APP_THEME.primaryColor}33`,
                            }}
                        >
                            Update Profile
                        </button>
                    </form>
                </div>
            </div>
            <Toaster position="bottom-center" />

            <style>
                {`
                    .active\\:scale-98:active {
                        transform: scale(0.98);
                    }
                `}
            </style>
        </>
    );
}

export default ProfileEdit;